import type { AppStore } from "@/store/AppStore";
import { AiToolkitNoteNotFoundError } from "@/modules/ai-toolkit/errors";
import type { UpdateNoteInput, UpdateNoteOutput } from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  updateNoteInputSchema,
  updateNoteOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import { buildNoteContentForUpdate } from "@/modules/ai-toolkit/tools/notes/note-mutation-tools.helpers";
import { UpdateNoteContentOverwritingFullStateDestructivelyOperation } from "@/store/sync/operations/notes/UpdateNoteContentOverwritingFullStateDestructivelyOperation";

export const updateNote = async ({
  store,
  input,
}: {
  store: AppStore;
  input: UpdateNoteInput;
}): Promise<UpdateNoteOutput> => {
  const note = await store.notes.getAsync(input.noteId);

  if (!note?.isAvailable) {
    throw new AiToolkitNoteNotFoundError({ noteId: input.noteId });
  }

  const noteContent = buildNoteContentForUpdate({
    mdx: input.mdx,
    fallbackTitle: note.title,
  });

  store.notes.getNoteQueue({ noteId: input.noteId }).push(
    new UpdateNoteContentOverwritingFullStateDestructivelyOperation({
      store,
      payload: {
        id: input.noteId,
        encoded_content: noteContent.encodedContent,
      },
      primaryLabel: noteContent.primaryLabel,
      secondaryLabel: noteContent.secondaryLabel,
      mediaKinds: noteContent.mediaKinds,
      mentions: noteContent.mentions,
      isEmpty: noteContent.isEmpty,
    })
  );
  store.interface.notifyReloadRequired(input.noteId);

  return {
    updated: true,
    noteId: input.noteId,
  };
};

export const updateNoteTool: AiToolkitToolDefinition<
  AiToolkitToolName.UpdateNote,
  UpdateNoteInput,
  UpdateNoteOutput
> = {
  name: AiToolkitToolName.UpdateNote,
  inputSchema: updateNoteInputSchema,
  outputSchema: updateNoteOutputSchema,
  strict: true,
  execute: updateNote,
};
